/**
 * @description
 * 1. tips框
 * 2. 充值金额渲染
 */

import { computed, ref, watch } from "@vue/composition-api"
import { createNamespacedHelpers } from "vuex-composition-helpers"
import { AppleGiftCardActions } from "../../store/apple-gift-card/actions"
import { AppleGiftCardGetters } from "../../store/apple-gift-card/getters"
import { AppleGiftCardMutations } from "../../store/apple-gift-card/mutations"
import { AppleGiftCardState } from "../../store/apple-gift-card/state"
import { GiftTaskConfigItem, REPORT } from "../../config"
import { TaskProcessQueryBodyInfo, TaskProcQueryBodyRuleItem } from "../../api"
import { checkTimeValid } from "../../helper"
import { cloneDeep } from "lodash"

export enum TASK_PROCESS_QUERY {
  NOT_REGISTER = 1,
  ERROR = 2,
  NO_CONFIG = 3
}

export interface GiftTaskConfigItemResult extends GiftTaskConfigItem {
  weekProcess?: TaskProcessQueryBodyInfo
  monthProcess?: TaskProcessQueryBodyInfo
  res?: TASK_PROCESS_QUERY
}

const { useState, useActions, useGetters } = createNamespacedHelpers<
  AppleGiftCardState,
  AppleGiftCardGetters,
  AppleGiftCardActions,
  AppleGiftCardMutations
>("appleGiftCard")

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default () => {
  const { isLoggedIn, giftTaskConfig } = useState(["isLoggedIn", "giftTaskConfig"])
  const { totalTaskProcessQuery, taskProcessQuery, reportUserAction } = useActions([
    "totalTaskProcessQuery",
    "taskProcQuery",
    "taskProcessQuery",
    "reportUserAction"
  ])
  const { giftTaskConfigItems } = useGetters(["giftTaskConfigItems"])

  const weekProcess = ref<TaskProcessQueryBodyInfo | null>(null)
  const monthProcess = ref<TaskProcessQueryBodyInfo | null>(null)
  const ccbProcessRequestFailed = ref(false)
  const hhbProcessRequestFailed = ref(false)
  const totalTask = ref<null | TaskProcQueryBodyRuleItem>(null)
  const selectedTab = ref<GiftTaskConfigItemResult>(giftTaskConfigItems.value[0])
  const isLoading = ref(true)
  const isInit = ref(false)

  const isValidTime = !!giftTaskConfigItems.value.length

  // 存储所有业务花花币任务进度
  const giftTaskConfigItemsProcessQueryResult = ref<GiftTaskConfigItemResult[]>(Array.from(giftTaskConfigItems.value))

  // 排序后的业务信息
  const sortedGiftTaskConfigItems = computed(() => {
    if (hhbProcessRequestFailed.value) return giftTaskConfigItemsProcessQueryResult.value
    const res = cloneDeep(giftTaskConfigItemsProcessQueryResult.value).sort((a, b) => {
      const idx1 = giftTaskConfigItemsProcessQueryResult.value.findIndex(i => i.offerId === a.offerId)
      const idx2 = giftTaskConfigItemsProcessQueryResult.value.findIndex(i => i.offerId === b.offerId)
      const aHasProcess = a.monthProcess || a.weekProcess
      const bHasProcess = b.monthProcess || b.weekProcess
      if ((aHasProcess && bHasProcess) || (!aHasProcess && !bHasProcess)) return idx1 - idx2
      if (aHasProcess && !bHasProcess) {
        return -1
      } else if (!aHasProcess && bHasProcess) return 1
      return 0
    })
    !isInit.value && handleSelectTab(res[0])
    return res
  })

  // 充充币进度展示
  const ccbScore = computed(() => {
    return {
      weekCcbScore: !weekProcess.value ? 0 : Number(weekProcess.value.total_score) / 100,
      monthCcbScore: !monthProcess.value ? 0 : Number(monthProcess.value.total_score) / 100
    }
  })

  const floatingWindowIsValid = computed(() => {
    const { floatingWindow } = giftTaskConfig.value
    return checkTimeValid(floatingWindow?.beginTime, floatingWindow?.endTime) && floatingWindow?.text
  })

  const handleSelectTab = (taskConfig: GiftTaskConfigItem) => {
    selectedTab.value = taskConfig
  }

  // 领取礼包后回显
  const handleGetTaskPresent = ({ taskId, monthTaskId, presentRank }: { taskId?: string; monthTaskId?: string; presentRank: string }) => {
    if (taskId) {
      // 周任务
      const result = giftTaskConfigItemsProcessQueryResult.value.find(res => res.taskId === taskId)
      if (!result?.weekProcess) return
      result.weekProcess.present_rank += `${result.weekProcess.present_rank ? "," : ""}${presentRank}`
    } else if (monthTaskId) {
      // 月任务
      const result = giftTaskConfigItemsProcessQueryResult.value.find(res => res.monthConfig?.monthTaskId === monthTaskId)
      if (!result?.monthProcess) return
      result.monthProcess.present_rank += `${result.monthProcess.present_rank ? "," : ""}${presentRank}`
    }
  }

  // 查询苹果卡充充币任务进度
  const handleTotalTaskProcessQuery = async () => {
    const { offerId, groupId, taskId, monthTaskId } = giftTaskConfig.value
    try {
      const res = await totalTaskProcessQuery({ offerId, groupId, taskId, monthTaskId })
      if (!res?.info?.task_info) return
      const { task_info } = res.info
      weekProcess.value = task_info.find(info => info.task_id === giftTaskConfig.value.taskId) || null
      monthProcess.value = task_info.find(info => info.task_id === giftTaskConfig.value.monthTaskId) || null
    } catch (e) {
      ccbProcessRequestFailed.value = true
      reportUserAction({
        iformat: REPORT.PAGEVIEW.TOTAL_TASK_PROCESS_QUERY_ERROR,
        resultInfo: e
      })
    }
  }

  // 查询业务花花币任务进度
  const handleTaskProcessQuery = async (taskConfig: GiftTaskConfigItem) => {
    if (!taskConfig) return { res: TASK_PROCESS_QUERY.NO_CONFIG }
    try {
      const { info } = await taskProcessQuery({ ...taskConfig, monthTaskId: taskConfig.monthConfig?.monthTaskId, timeout: 3000 })
      const weekProcess = info[taskConfig.groupId]?.find(subTask => subTask.task_id === taskConfig.taskId)
      const monthProcess = info[taskConfig.groupId]?.find(subTask => subTask.task_id === taskConfig.monthConfig?.monthTaskId)
      if (!weekProcess && !monthProcess) throw new Error("No task")
      return { weekProcess, monthProcess }
    } catch (e: any) {
      // 对未注册异常进行过滤，不属于requestFailed
      if (e?.ret === 1295) return { res: TASK_PROCESS_QUERY.NOT_REGISTER }
      // if (e?.ret === 1414) {
      //   requestFailedMsg = "活动已过期"
      //   return
      // }
      return { res: TASK_PROCESS_QUERY.ERROR }
    }
  }

  // 初始化所有业务花花币任务进度
  const initTaskProcessQuery = async () => {
    const requests = giftTaskConfigItems.value.map(item => handleTaskProcessQuery(item))
    const _res = await Promise.all(requests) // 使用Promise.all的原因是请求函数已经catch了错误
    giftTaskConfigItemsProcessQueryResult.value = cloneDeep(giftTaskConfigItemsProcessQueryResult.value).map((i, idx) => ({
      ...i,
      ..._res[idx]
    }))
    hhbProcessRequestFailed.value = _res.findIndex(i => !!i.res && i.res !== TASK_PROCESS_QUERY.NOT_REGISTER) !== -1 // 如果请求函数返回了res, 说明有错误
  }

  isValidTime &&
    watch(
      isLoggedIn,
      async () => {
        if (isLoggedIn.value) {
          await Promise.all([handleTotalTaskProcessQuery(), initTaskProcessQuery()])
          isLoading.value = false
          setTimeout(() => {
            isInit.value = true
          })
        }
      },
      {
        immediate: true
      }
    )

  return {
    isLoggedIn,
    giftTaskConfig,
    giftTaskConfigItems,
    sortedGiftTaskConfigItems,
    isValidTime,
    floatingWindowIsValid,
    weekProcess,
    monthProcess,
    ccbScore,
    totalTask,
    selectedTab,
    isLoading,
    TASK_PROCESS_QUERY,
    handleGetTaskPresent,
    handleSelectTab
  }
}
